import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Webcam from "react-webcam";
import API_UTIL from "../../../services/AuthIntereptor";
import AppBar from "../../../components/AppBar/AppBar";
import Footer from "../../../components/Footer/Footer";
import styled from "styled-components";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import OTPAuth from "../OTPAuth/OTPAuth";
import { toast } from "react-toastify";
import { Button, Input, ModalShell, Icons } from "../../../components/ui";
import logofull from "../../../media/images/logofull.png";
import DocuSealConsentModal from "./DocuSealConsentModal";

/* Brand palette (matches Tailwind brand-* tokens) */
const BRAND = "#2563eb";

/* react-phone-input-2 styled to match the design system */
const StyledPhoneInput = styled.div`
  .react-tel-input {
    width: 100%;
    font-family: inherit;
  }

  .form-control {
    width: 100% !important;
    height: 46px !important;
    border-radius: 8px !important;
    border: 1px solid #e5e7eb !important;
    font-size: 14px !important;
    background: #ffffff !important;
    color: #0f172a !important;
    padding-left: 52px !important;
    transition: border-color 0.15s, box-shadow 0.15s;

    &:hover {
      border-color: #d1d5db !important;
    }
    &:focus {
      border-color: ${BRAND} !important;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.3) !important;
    }
  }

  .flag-dropdown {
    background: transparent !important;
    border: none !important;
    border-radius: 8px 0 0 8px !important;
  }
  .flag-dropdown.open,
  .selected-flag.open,
  .selected-flag:hover,
  .selected-flag:focus {
    background: transparent !important;
    border-radius: 8px 0 0 8px !important;
  }
  .selected-flag {
    width: 46px !important;
    padding: 0 0 0 12px !important;
    border-radius: 8px 0 0 8px !important;
  }
  .country-list {
    border-radius: 8px !important;
    margin-top: 4px !important;
    box-shadow: 0 8px 24px -8px rgba(15, 23, 42, 0.18) !important;
    .country.highlight,
    .country:hover {
      background: #eff6ff !important;
    }
    .search {
      padding: 8px !important;
    }
    .search-box {
      border: 1px solid #e5e7eb !important;
      border-radius: 6px !important;
    }
  }
`;

function Login({ name, onLoginSuccess, showAppBar = false }) {
  const { eventName } = useParams();
  const [isNewUser, setIsNewUser] = useState(false);
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const webcamRef = useRef(null);
  const [imgSrc, setImgSrc] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(true);
  const isToastDisp = useRef(false);
  const [isCaptureEnabled, setIsCaptureEnabled] = useState(true);
  const [otpSentTime, setOtpSentTime] = useState(null);
  const [showOTP, setShowOTP] = useState(false);
  const [fullPhoneValue, setFullPhoneValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState("");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [showPortraitModal, setShowPortraitModal] = useState(false);
  // Web biometric-consent (DocuSeal) signing gate
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentPhone, setConsentPhone] = useState("");

  const popularDomains = ["@gmail.com", "@yahoo.com", "@outlook.com", "@hotmail.com", "@aol.com"];

  const emailOptions = popularDomains
    .map((domain) => {
      const username = email.split("@")[0] || "";
      return email.includes("@") ? "" : `${username}${domain}`;
    })
    .filter((option) => option);

  const videoConstraints = {
    width: 350,
    height: 250,
    facingMode: "user",
  };

  let location = useLocation();
  const navigate = useNavigate();
  const fromLocation = location.state?.from;
  const fromUrl = fromLocation?.pathname
    ? `${fromLocation.pathname}${fromLocation.search || ""}`
    : eventName
    ? `/photosv1/${eventName}/upcoming`
    : "/event?tab=attended";

  useEffect(() => {
    document.body.classList.add("login-screen-active");
    return () => {
      document.body.classList.remove("login-screen-active");
    };
  }, []);

  useEffect(() => {
    const userPhoneNumber = localStorage.getItem("userPhoneNumber");
    if (!userPhoneNumber) return;
    // Gate every returning user on the consent agreement — including users bounced here
    // from a protected deep-link (from === a /photos route). Only after consent is
    // confirmed do we run the original auto-navigation.
    const cameFromPhotos = location.state?.from?.pathname?.includes("/photos");
    checkWebConsent(userPhoneNumber).then((signed) => {
      if (!signed) {
        setConsentPhone(userPhoneNumber);
        setShowConsentModal(true);
        return;
      }
      localStorage.setItem("webConsentSigned", "true");
      if (!cameFromPhotos && eventName) {
        mapUserToEvent(userPhoneNumber);
      }
      navigate(fromUrl);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location.state, fromUrl]);

  useEffect(() => {
    if (isToastDisp.current) return;
    isToastDisp.current = true;
  }, []);

  const capture = async () => {
    if (isCaptureEnabled) {
      const imageSrc = webcamRef.current.getScreenshot();
      setImgSrc(imageSrc);
    }
  };

  const mapUserToEvent = async (userPhoneNumber) => {
    try {
      await API_UTIL.post("/mapUserToEvent", {
        event_id: eventName,
        user_phone_number: userPhoneNumber,
      });
    } catch (error) {
      console.error("Error mapping event to user:", error);
    }
  };

  const retake = () => {
    setImgSrc(null);
    setIsCaptureEnabled(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const fullPhoneNumber = countryCode + phoneNumber;
    try {
      await createOrLoginUser(fullPhoneNumber);
    } catch (error) {
      console.error("Error sending OTP:", error);
    }
  };

  const createOrLoginUser = async (fullPhoneNumber) => {
    let userSource = "ImageShieldPhotoShare";
    let role = "user";
    try {
      const response = await API_UTIL.post(`/createUser`, {
        username: fullPhoneNumber,
        eventName: eventName,
        userSource: userSource,
        role: role,
      });
      setIsPhoneNumberValid(true);

      if (response.status === 201) {
        try {
          const userDetailsResponse = await API_UTIL.get(`/getUserDetails/${fullPhoneNumber}`);
          const userData = userDetailsResponse.data;

          if (userData && userData.email) {
            localStorage.setItem("userPhoneNumber", fullPhoneNumber);
            setShowEmailModal(false);
            setIsNewUser(true);
          } else {
            setShowEmailModal(true);
            setIsNewUser(true);
          }
        } catch (error) {
          console.error("Error fetching user details:", error);
          setShowEmailModal(true);
          setIsNewUser(true);
        }
      } else if (response.status === 200 && response.data.status === "exists") {
        if (!response.data.email) {
          setShowEmailModal(true);
          setIsNewUser(false);
        } else {
          localStorage.setItem("userPhoneNumber", fullPhoneNumber);
          await checkUserPortraitAndNavigate(fullPhoneNumber);
        }
      }
    } catch (error) {
      console.error("Error creating/logging in user:", error);
    }
  };

  const checkUserPortraitAndNavigate = async (fullPhoneNumber) => {
    try {
      const userDetailsResponse = await API_UTIL.get(`/getUserDetails/${fullPhoneNumber}`);
      const userData = userDetailsResponse.data;

      if (userData && userData.potrait_s3_url) {
        localStorage.setItem("userPhoneNumber", fullPhoneNumber);
        localStorage.setItem("potrait_s3_url", userData.potrait_s3_url);
        handleNavigation(fullPhoneNumber);
      } else {
        localStorage.setItem("userPhoneNumber", fullPhoneNumber);
        setShowPortraitModal(true);
        setIsNewUser(false);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      localStorage.setItem("userPhoneNumber", fullPhoneNumber);
      setShowPortraitModal(true);
      setIsNewUser(false);
    }
  };

  const handleEmailSubmit = async () => {
    if (!email || !validateEmail(email)) {
      setEmailError("Please enter a valid email address (e.g., user@example.com)");
      return;
    }

    const fullPhoneNumber = countryCode + phoneNumber;
    try {
      await API_UTIL.post("/updateUserEmail", {
        userPhoneNumber: fullPhoneNumber,
        email: email,
      });

      setShowEmailModal(false);

      if (isNewUser) {
        localStorage.setItem("userPhoneNumber", fullPhoneNumber);
      } else {
        localStorage.setItem("userPhoneNumber", fullPhoneNumber);
        await checkUserPortraitAndNavigate(fullPhoneNumber);
      }
    } catch (error) {
      console.error("Error updating email:", error);
      toast.error("Failed to update email. Please try again.");
    }
  };

  const validateEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleEmailInput = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (newEmail && !validateEmail(newEmail)) {
      setEmailError("Please enter a valid email address (e.g., user@example.com)");
    } else {
      setEmailError("");
    }
  };

  const handleOTPVerify = async (otpString) => {
    const fullPhoneNumber = countryCode + phoneNumber;
    const login_platform = "ImageShield";

    try {
      const response = await API_UTIL.post("/verifyOTP", {
        phoneNumber: fullPhoneNumber,
        otp: otpString,
        login_platform,
      });

      if (response.data.success === false) {
        return {
          success: false,
          message: response.data.message,
          remainingAttempts: response.data.remainingAttempts,
        };
      }

      await createOrLoginUser(fullPhoneNumber);
      setShowOTP(false);
      return { success: true };
    } catch (error) {
      console.error("Error verifying OTP:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Error verifying OTP",
      };
    }
  };

  const handleOTPResend = async () => {
    const fullPhoneNumber = countryCode + phoneNumber;
    try {
      const response = await API_UTIL.post("/initiateOTP", { phoneNumber: fullPhoneNumber });
      if (response.status === 200) {
        setShowOTP(true);
        setOtpSentTime(Date.now());
      }
    } catch (error) {
      console.error("Error resending OTP:", error);
    }
  };

  // Performs the actual post-login navigation (unchanged original behavior).
  const doNavigate = async (fullPhoneNumber) => {
    if (typeof fromUrl === "string" && fromUrl.includes("photos") && !fromUrl.includes("upcoming")) {
      try {
        const urlArray = fromUrl.split("/");
        const response = await API_UTIL.post(`/userIdPhoneNumberMapping`, {
          phoneNumber: fullPhoneNumber,
          eventName: urlArray[urlArray.length - 2],
          userId: urlArray[urlArray.length - 1],
        });
        if (response.status === 200) {
          localStorage.setItem("userPhoneNumber", fullPhoneNumber);
          navigate(fromUrl);
        }
      } catch (error) {
        console.log("Error in mapping the userId and phone number");
      }
    } else {
      navigate(fromUrl);
    }
    localStorage.setItem("userPhoneNumber", fullPhoneNumber);
  };

  // Checks whether the user has signed the web biometric-consent agreement.
  // Fails closed (returns false) so a check error gates the user into signing.
  const checkWebConsent = async (fullPhoneNumber) => {
    try {
      const res = await API_UTIL.get(
        `/api/web-consent/status/${encodeURIComponent(fullPhoneNumber)}`
      );
      return !!(res.data && res.data.webConsentSigned);
    } catch (error) {
      console.error("Error checking web consent status:", error);
      return false;
    }
  };

  // Gate: only navigate once the agreement is signed; otherwise open the signing modal.
  const handleNavigation = async (fullPhoneNumber) => {
    const signed = await checkWebConsent(fullPhoneNumber);
    if (signed) {
      localStorage.setItem("webConsentSigned", "true");
      await doNavigate(fullPhoneNumber);
    } else {
      setConsentPhone(fullPhoneNumber);
      setShowConsentModal(true);
    }
  };

  // Called by the consent modal once signing is verified server-side.
  const onConsentSigned = async () => {
    setShowConsentModal(false);
    localStorage.setItem("webConsentSigned", "true");
    if (consentPhone) {
      await doNavigate(consentPhone);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.keyCode === 13) {
      event.preventDefault();
      handleSubmit(event);
    }
  };

  const uploadPhoto = async () => {
    if (termsAccepted) {
      const fullPhoneNumber = countryCode + phoneNumber;
      const formData = new FormData();
      formData.append("image", imgSrc);
      formData.append("username", fullPhoneNumber);
      formData.append("event", eventName);
      setIsSubmitting(true);

      try {
        const uploadResponse = await API_UTIL.post(`/uploadUserPotrait`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (uploadResponse.data && uploadResponse.data.potrait_s3_url) {
          localStorage.setItem("potrait_s3_url", uploadResponse.data.potrait_s3_url);
        }

        localStorage.setItem("userPhoneNumber", fullPhoneNumber);
        handleNavigation(fullPhoneNumber);
      } catch (error) {
        console.error("Error uploading image:", error);
        toast.error("Failed to upload photo. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const uploadPhotoFromModal = async () => {
    if (termsAccepted) {
      const fullPhoneNumber = localStorage.getItem("userPhoneNumber");
      const formData = new FormData();
      formData.append("image", imgSrc);
      formData.append("username", fullPhoneNumber);
      formData.append("event", eventName);
      setIsSubmitting(true);

      try {
        const uploadResponse = await API_UTIL.post(`/uploadUserPotrait`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (uploadResponse.data && uploadResponse.data.potrait_s3_url) {
          localStorage.setItem("potrait_s3_url", uploadResponse.data.potrait_s3_url);
        }

        setShowPortraitModal(false);
        handleNavigation(fullPhoneNumber);
      } catch (error) {
        console.error("Error uploading image:", error);
        toast.error("Failed to upload photo. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const TermsCheckbox = (
    <label className="flex items-center gap-2 text-sm text-slate-600 mt-1">
      <input
        type="checkbox"
        checked={termsAccepted}
        onChange={(e) => setTermsAccepted(e.target.checked)}
        className="w-4 h-4 accent-brand-600"
      />
      <span>
        Accept{" "}
        <a
          href="/TermsAndConditions"
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand-600 hover:underline"
        >
          terms and conditions
        </a>
      </span>
    </label>
  );

  const howSteps = [
    { n: "01", icon: Icons.QR, title: "Scan QR Code", desc: "Scan QR code at the event location for quick access" },
    { n: "02", icon: Icons.Camera, title: "Take a Selfie", desc: "Click a selfie to help us identify you in event photos" },
    { n: "03", icon: Icons.Cpu, title: "AI Recognition", desc: "Our AI identifies you with 99% accuracy in all photos" },
    { n: "04", icon: Icons.Message, title: "Get Your Photos", desc: "Receive photos directly on your registered number" },
  ];

  return (
    <div className="login-screen min-h-screen min-h-[100dvh] flex flex-col bg-white text-slate-900">
      {showAppBar ? (
        <AppBar showLogout={false} />
      ) : (
        <header className="z-30 border-b border-gray-200 bg-white">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-center">
            <img src={logofull} alt="ImageShield" className="h-10 w-auto" />
          </div>
        </header>
      )}

      <main className="flex-1">
        {/* Hero band + login card */}
        <div className="bg-gradient-to-b from-brand-50/60 to-white border-b border-gray-100">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-14 pb-16">
            <div className="flex justify-center">
              <div className="w-full max-w-md">
                <div className="bg-white border border-gray-200 rounded-2xl shadow-card p-7 sm:p-8">
                  {/* Phone entry */}
                  {!isPhoneNumberValid && !showOTP && !showEmailModal && (
                    <>
                      <h2 className="text-lg font-bold text-slate-900">Sign in to ImageShield</h2>
                      <p className="text-sm text-slate-500 mt-1">
                        We'll send a code to your phone. No password needed.
                      </p>
                      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                        <div>
                          <span className="block text-xs font-semibold text-slate-700 mb-1.5">Phone number</span>
                          <StyledPhoneInput>
                            <PhoneInput
                              country={"us"}
                              value={fullPhoneValue}
                              onChange={(value, data) => {
                                const inputElement = document.querySelector(".react-tel-input input");
                                const selectionStart = inputElement?.selectionStart || 0;
                                const selectionEnd = inputElement?.selectionEnd || 0;
                                const isSelected = selectionEnd - selectionStart > 0;

                                if (isSelected) {
                                  const beforeSelection = value.slice(0, data.dialCode.length + selectionStart);
                                  const afterSelection = value.slice(data.dialCode.length + selectionEnd);
                                  value = beforeSelection + afterSelection;
                                }

                                setFullPhoneValue(value);

                                if (value.length <= data.dialCode.length) {
                                  setCountryCode("+" + data.dialCode);
                                }

                                const numberWithoutCode = value.slice(data.dialCode.length).replace(/\D/g, "");
                                setPhoneNumber(numberWithoutCode);

                                if (!numberWithoutCode) {
                                  setPhoneNumberError("Phone number is required");
                                } else {
                                  setPhoneNumberError("");
                                }
                              }}
                              onCountryChange={(code, data) => {
                                setCountryCode("+" + data.dialCode);
                                setFullPhoneValue(data.dialCode + phoneNumber);
                              }}
                              enableSearch
                              searchPlaceholder="Search countries..."
                              inputProps={{
                                name: "phone",
                                required: true,
                                placeholder: "(555) 014-2230",
                                onKeyDown: handleKeyDown,
                              }}
                              specialLabel=""
                              countryCodeEditable={false}
                              disableCountryCode={false}
                              preserveOrder={["onCountryChange", "onChange"]}
                            />
                          </StyledPhoneInput>
                          {phoneNumberError && (
                            <div className="text-[11px] text-red-600 mt-1">{phoneNumberError}</div>
                          )}
                        </div>
                        <Button type="submit" className="w-full">
                          Continue
                        </Button>
                      </form>
                    </>
                  )}

                  {/* OTP step */}
                  {showOTP && (
                    <div className="fade-in">
                      <OTPAuth
                        phoneNumber={countryCode + phoneNumber}
                        onVerify={handleOTPVerify}
                        onResend={handleOTPResend}
                        otpSentTime={otpSentTime}
                      />
                    </div>
                  )}

                  {/* Selfie capture for new user */}
                  {isNewUser && !showEmailModal && (
                    <div className="fade-in">
                      <h2 className="text-lg font-bold text-slate-900">Take a quick selfie</h2>
                      <p className="text-sm text-slate-500 mt-1 mb-4">
                        Perfect lighting, perfect you — let's snap that selfie! :)
                      </p>
                      {imgSrc ? (
                        <img
                          src={imgSrc}
                          alt="captured"
                          className="w-full max-w-[350px] mx-auto mb-3 block rounded-xl shadow-card"
                        />
                      ) : (
                        <Webcam
                          audio={false}
                          ref={webcamRef}
                          screenshotFormat="image/jpeg"
                          videoConstraints={videoConstraints}
                          mirrored={true}
                          className="w-full max-w-[350px] mx-auto mb-3 block rounded-xl shadow-card"
                        />
                      )}
                      {imgSrc ? (
                        <div className="space-y-2">
                          <Button
                            className="w-full"
                            onClick={uploadPhoto}
                            disabled={!termsAccepted || isSubmitting}
                          >
                            {isSubmitting ? "Submitting…" : "Submit Photo"}
                          </Button>
                          <Button variant="secondary" className="w-full" onClick={retake}>
                            Retake photo
                          </Button>
                          {TermsCheckbox}
                        </div>
                      ) : (
                        isCaptureEnabled && (
                          <Button className="w-full" onClick={capture}>
                            Capture photo
                          </Button>
                        )
                      )}
                    </div>
                  )}

                  {/* Agreement text */}
                  <p className="text-center text-[11px] text-slate-500 leading-relaxed mt-5">
                    By continuing, you agree to our{" "}
                    <a
                      href="https://www.imageshield.com/ai-terms-and-conditions"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-600 hover:underline"
                    >
                      Terms and Conditions
                    </a>{" "}
                    and{" "}
                    <a
                      href="https://www.imageshield.com/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-600 hover:underline"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* How it works */}
        <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16">
          <div className="mb-8">
            <span className="text-xs font-semibold tracking-wider text-brand-600 uppercase">How it works</span>
            <h3 className="text-2xl font-bold text-slate-900 mt-1">Four steps, end-to-end.</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {howSteps.map((s) => {
              const IconC = s.icon;
              return (
                <div
                  key={s.n}
                  className="relative bg-white border border-gray-200 rounded-xl p-6 hover:shadow-card-hover hover:-translate-y-0.5 transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="w-11 h-11 rounded-lg bg-brand-50 border border-brand-100 grid place-items-center text-brand-600">
                      <IconC className="w-5 h-5" />
                    </div>
                    <span className="font-mono text-[11px] text-slate-300 tracking-widest">{s.n}</span>
                  </div>
                  <h4 className="mt-4 font-bold text-slate-900">{s.title}</h4>
                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </section>
      </main>

      {/* Biometric consent (DocuSeal) signing gate */}
      <DocuSealConsentModal
        open={showConsentModal}
        phone={consentPhone}
        name={typeof name === "string" ? name : ""}
        onSigned={onConsentSigned}
      />

      {/* Email capture modal */}
      <ModalShell open={showEmailModal} onClose={() => {}} maxWidth="max-w-md">
        <div className="p-6">
          <h2 className="text-lg font-bold text-slate-900">Stay Updated</h2>
          <p className="text-sm text-slate-500 mt-1 mb-4">
            Share your email to receive your photos if your phone number isn't supported.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleEmailSubmit();
            }}
            className="space-y-3"
          >
            <Input
              label="Email"
              type="email"
              required
              value={email}
              onChange={handleEmailInput}
              list="login-email-suggestions"
              placeholder="you@example.com"
              error={emailError}
            />
            <datalist id="login-email-suggestions">
              {emailOptions.map((o) => (
                <option key={o} value={o} />
              ))}
            </datalist>
            <Button type="submit" className="w-full" disabled={!!emailError}>
              Submit
            </Button>
          </form>
        </div>
      </ModalShell>

      {/* Portrait modal (existing user without selfie) */}
      <ModalShell open={showPortraitModal} onClose={() => {}} maxWidth="max-w-md">
        <div className="p-6">
          <h2 className="text-lg font-bold text-slate-900">Selfie Required</h2>
          <p className="text-sm text-slate-500 mt-1 mb-4">
            Perfect lighting, perfect you — let's snap that selfie! :)
          </p>
          {imgSrc ? (
            <img
              src={imgSrc}
              alt="captured"
              className="w-full max-w-[350px] mx-auto mb-3 block rounded-xl shadow-card"
            />
          ) : (
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              mirrored={true}
              className="w-full max-w-[350px] mx-auto mb-3 block rounded-xl shadow-card"
            />
          )}
          {imgSrc ? (
            <div className="space-y-2">
              <Button
                className="w-full"
                onClick={uploadPhotoFromModal}
                disabled={!termsAccepted || isSubmitting}
              >
                {isSubmitting ? "Submitting…" : "Submit Photo"}
              </Button>
              <Button variant="secondary" className="w-full" onClick={retake}>
                Retake photo
              </Button>
              {TermsCheckbox}
            </div>
          ) : (
            <Button className="w-full" onClick={capture}>
              Capture photo
            </Button>
          )}
        </div>
      </ModalShell>

      <Footer />
    </div>
  );
}

export default Login;
